
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const SUPPORT_EMAIL = "support@taptotable.co";

const nextSteps = [
  {
    title: "We review your restaurant information",
    body: "We’ll review the details you submitted and gather the available restaurant information needed to begin building your page.",
  },
  {
    title: "We build your TapToTable Page",
    body: "We’ll create your TapToTable Page using your restaurant’s branding, photos, menu, and available business information. Your page can feature an Offer / Promotion, Event, or Featured Item, depending on what you want to promote.",
  },
  {
    title: "You receive a private review link",
    body: "We’ll email you a private link so you can view the page exactly as customers will. If you provided an Instagram handle, we may also message you there when your page is ready.",
  },
  {
    title: "You request changes or activate",
    body: "Request any changes you’d like. Once you’re satisfied, activate your first restaurant location for $59/month. Additional locations under the same ownership are $39/month each. Your subscription includes a secure, easy-to-use TapToTable Dashboard where you can create and update Promotions, Events, and Featured Items, manage restaurant details, and monitor customer activity and supported analytics. Each location has its own page, login, and dashboard.",
  },
];

export default function Thanks() {
  const location = useLocation();
  const restaurantName = location.state?.restaurant_name;

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="thanks-page">
      <Navbar />

      <main className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border-2 border-[#00E5FF] bg-[#00E5FF]/10 text-3xl text-[#00E5FF] shadow-[0_0_40px_rgba(0,229,255,0.3)]"
            aria-hidden="true"
          >
            ✓
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mt-8 label-caps"
          >
            Page Request Received
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]"
            data-testid="thanks-heading"
          >
            {restaurantName ? (
              `Thanks, ${restaurantName}!`
            ) : (
              <>
                <span className="block">Your TapToTable Page</span>
                <span className="block">Request Is In!</span>
              </>
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-5 text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl mx-auto"
          >
            We&apos;ve received your restaurant information and will begin
            building your TapToTable Page.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="mt-3 text-sm font-medium text-zinc-300"
          >
            We typically send your private review link within 1–2 business days.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 rounded-2xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 p-5 text-left"
            data-testid="thanks-payment-note"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#00E5FF]/15 font-semibold text-[#00E5FF]">
                ✓
              </div>

              <div>
                <div className="font-display font-semibold text-white">
                  No payment is required now
                </div>

                <p className="mt-1 text-sm leading-relaxed text-zinc-300">
                  We&apos;ll build your TapToTable Page first and send you a
                  private review link. Review your page, request any changes,
                  and activate only when you&apos;re completely satisfied.
                  Your first restaurant location is $59/month, with each
                  additional eligible location under the same ownership
                  available for $39/month. There are no setup fees or long-term
                  contracts.
                </p>

                <p className="mt-3 text-sm leading-relaxed text-zinc-300">
                  Your subscription includes hosting, a secure, easy-to-use
                  TapToTable Dashboard, Promotions, Events, and Featured Items,
                  restaurant photo hosting, supported customer activity
                  analytics, ongoing support, and the ability to cancel anytime.
                  Each restaurant location has its own page, login, and dashboard.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-12"
            aria-labelledby="next-steps-heading"
          >
            <div className="text-left">
              <div className="label-caps">What Happens Next</div>

              <h2
                id="next-steps-heading"
                className="mt-2 font-display text-2xl font-semibold text-white sm:text-3xl"
              >
                We&apos;ll Take It From Here
              </h2>

              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                Here&apos;s what happens between submitting your restaurant
                information and activating your TapToTable Page.
              </p>
            </div>

            <div className="mt-6 grid gap-4 text-left">
              {nextSteps.map((step, index) => (
                <div
                  key={step.title}
                  className="flex items-start gap-5 rounded-2xl border border-white/5 bg-[#121214] p-6"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#00E5FF]/10 font-semibold text-[#00E5FF]">
                    {index + 1}
                  </div>

                  <div>
                    <h3 className="font-display font-semibold text-base text-white">
                      {step.title}
                    </h3>

                    <p className="mt-1 text-sm leading-relaxed text-zinc-400">
                      {step.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.68 }}
            className="mt-8 rounded-2xl border border-violet-400/20 bg-violet-400/[0.05] p-6 text-left"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
              After Activation
            </div>

            <h2 className="mt-2 font-display text-lg font-semibold text-white">
              Your Page Can Change With Your Restaurant
            </h2>

            <p className="mt-2 text-sm leading-relaxed text-zinc-400">
              Use your secure, easy-to-use TapToTable Dashboard on your phone
              or computer to switch between Promotions, Events, and Featured
              Items, update photos and restaurant details, change customer
              action links, and keep your page current as your restaurant
              changes.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.72 }}
            className="mt-8 rounded-2xl border border-[#00E5FF]/20 bg-[#00E5FF]/[0.06] p-6"
          >
            <p className="text-sm leading-relaxed text-zinc-300">
              Watch your email for your private review link. If you do not see
              it within 1–2 business days, check your spam or promotions folder,
              then contact us for help.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.76 }}
            className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6"
          >
            <h2 className="font-display text-lg font-semibold text-white">
              Want us to include anything else?
            </h2>

            <p className="mt-2 text-sm leading-relaxed text-zinc-400">
              Email additional photos, menu updates, Featured Item details,
              event information, branding, logos, or anything else
              you&apos;d like included on your TapToTable Page to{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-[#00E5FF] hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
              . Include your restaurant name in the subject line.
            </p>
          </motion.div>

          <div className="mt-10">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-zinc-400 transition-colors hover:text-white"
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
